import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';

import { GetArticleCommentsByID } from '@root/modules/article/types/article';
import getArticleCommentsByID from '@root/modules/article/graphql/getArticleCommentsByID.graphql';

import { ArticleCommentsAdapter } from '../utils/ArticleAdapter';

type ServiceVariables = { id: string | number };

type FetchVariables = GetArticleCommentsByID['variables'];

type Response = GetArticleCommentsByID['data'];

export default class ArticleForCommentsService extends Service implements ServiceInterface {
  private fixVariables({ id }: ServiceVariables) {
    return {
      id: Number(id),
    };
  }

  private async fetchArticleForComments(variables: FetchVariables) {
    const apiProvider = this.createProvider('GraphQL');
    apiProvider.selectAPI('content-api-v3');
    apiProvider.setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

    const options = Object.assign({ query: getArticleCommentsByID }, { variables });

    try {
      const response = await apiProvider.request<Response>(options);

      if (!(response.article.data.length > 0)) {
        this.handleError();
      }

      const articleAdapter = new ArticleCommentsAdapter(response.article.data[0]);
      const article = articleAdapter.adapt();

      return {
        article,
      };
    } catch (error) {
      process.sentry &&
        process.sentry.captureException(`fetchArticleForComments error: ${error}`, {
          contexts: { data: { variables } },
          tags: { 'process.server': process.server ? 'server' : 'client' },
        });

      return { article: null };
    }
  }

  public handleError() {
    throw {
      message: 'article.error.not_found',
      statusCode: 404,
    };
  }

  public async fetch(variables: ServiceVariables) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError();
    }

    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchArticleForComments(fixedVariables);

    return response;
  }
}

export function fetchArticleForComments(variables: ServiceVariables) {
  const articleForCommentsService = new ArticleForCommentsService();
  return articleForCommentsService.fetch(variables);
}
